import React, { useCallback, useEffect, useMemo } from "react";
import { Box, Button, Grid } from "@mantine/core";
import { useStyles } from "../../styles/styles";
import { SinglePageContent } from "./sections/SinglePageContent";
import { SectionContent } from "./sections/SectionContent";
import { IconEye } from "@tabler/icons-react";
import type { Section } from "../../types";
import { useResizablePanel } from "../../hooks/useResizablePanel";
import { DocumentViewerPanel } from "./sections/DocumentViewerPanel";
import { useProfileStore } from "../../store/profileStore";
import ErrorFieldTracker from "../Diagnostics/ErrorFieldTracker";
import { useDiagnosticStore } from "../Diagnostics/store";

interface TaxInfoReviewProps {
  contentData?: any;
  sections?: Section[];
  sectionsRefs?: React.RefObject<HTMLDivElement>[];
}

const ViewDocumentButton: React.FC<{
  isSplitView: boolean;
  toggleSplitView: () => void;
}> = ({ isSplitView, toggleSplitView }) => (
  <div
    style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}
  >
    <Button
      variant={isSplitView ? "filled" : "outline"}
      leftSection={<IconEye size={14} />}
      onClick={toggleSplitView}
    >
      {isSplitView ? "Close Document View" : "View Document"}
    </Button>
  </div>
);

const TaxInfoReview: React.FC<TaxInfoReviewProps> = ({
  contentData = {},
  sections,
  sectionsRefs,
}: TaxInfoReviewProps) => {
  const { classes } = useStyles();
  const { width: leftPanelWidth, startResizing } = useResizablePanel();
  const isVeryFirstPage = true;
  const {
    isSplitView,
    setIsSplitView,
    selectedDoc,
    diagnosticErrors,
    fetchOCRDataForDocument,
  } = useProfileStore();

  // Subscribe to specific parts of the store to get reactive updates
  const selectedExtractionIds = useProfileStore(
    (state) => state.selectedExtractionIds
  );
  const ocrDataCache = useProfileStore((state) => state.ocrDataCache);

  const { selectedDiagnosticField, setSelectedDiagnosticField } =
    useDiagnosticStore();
  const docId = selectedDoc?.docId;

  // Build cache key for OCR data
  const selectedExtractionId = docId ? selectedExtractionIds[docId] : undefined;
  const cacheKey = selectedExtractionId
    ? `${docId}_${selectedExtractionId}`
    : (docId ?? "");
  const selectedOCRData = ocrDataCache[cacheKey];

  // Lazy-load OCR data from API when document or extraction changes
  useEffect(() => {
    if (docId) {
      fetchOCRDataForDocument(docId, selectedExtractionId);
    }
  }, [docId, selectedExtractionId, fetchOCRDataForDocument]);

  // Refetch function for manual refresh
  const refetchOCR = useCallback(async () => {
    if (docId) {
      fetchOCRDataForDocument(docId, selectedExtractionId);
    }
  }, [docId, selectedExtractionId, fetchOCRDataForDocument]);

  const hasErrors = Object.keys(diagnosticErrors).length > 0;

  // Clear selectedDiagnosticField when there are no errors
  useEffect(() => {
    if (!hasErrors && selectedDiagnosticField) {
      setSelectedDiagnosticField(null);
    }
  }, [hasErrors, selectedDiagnosticField, setSelectedDiagnosticField]);

  // Memoize the content rendering to prevent unnecessary re-renders
  const contentElement = useMemo(() => {
    if (selectedOCRData) {
      return (
        <SinglePageContent
          key={`ocr-${docId}`}
          contentData={selectedOCRData?.data}
          classes={classes}
          refetchOCR={refetchOCR}
        />
      );
    }

    if (sections?.length) {
      return (
        <>
          {sections.map((section: Section, index: number) => (
            <SectionContent
              key={section.key}
              section={section}
              sectionRef={sectionsRefs?.[index]}
              contentData={contentData}
              isVeryFirstPage={isVeryFirstPage}
              classes={classes}
            />
          ))}
        </>
      );
    }

    return (
      <SinglePageContent
        contentData={contentData}
        classes={classes}
        refetchOCR={refetchOCR}
      />
    );
  }, [
    selectedOCRData,
    docId,
    sections,
    sectionsRefs,
    contentData,
    isVeryFirstPage,
    classes,
    refetchOCR,
  ]);

  const toggleSplitView = useCallback(() => {
    setIsSplitView(!isSplitView);
  }, [isSplitView, setIsSplitView]);

  return (
    <Box className={classes.container}>
      {selectedDoc?.presignedUrl && (
        <ViewDocumentButton
          isSplitView={isSplitView}
          toggleSplitView={toggleSplitView}
        />
      )}

      {isSplitView ? (
        <Grid gutter={0} style={{ minHeight: "calc(100vh - 87px)" }}>
          <Grid.Col
            span={(leftPanelWidth / 100) * 12}
            style={{
              position: "relative",
              paddingRight: 4,
            }}
          >
            {contentElement}
            <div
              className={classes.resizeHandle}
              onMouseDown={(e) => startResizing(e, classes)}
            />
          </Grid.Col>
          <Grid.Col
            span={((100 - leftPanelWidth) / 100) * 12}
            style={{
              borderLeft: "1px solid #e0e0e0",
            }}
          >
            {selectedDoc && (
              <DocumentViewerPanel
                doc={selectedDoc}
                onClose={() => setIsSplitView(false)}
              />
            )}
          </Grid.Col>
        </Grid>
      ) : (
        contentElement
      )}
      {hasErrors && selectedDiagnosticField && (
        <ErrorFieldTracker onClose={() => setSelectedDiagnosticField(null)} />
      )}
    </Box>
  );
};

export default TaxInfoReview;
